/**
 * Alta / edición de una cuenta del censo (F6, admin): dueño (del community map),
 * área, destino, estado, uso, MFA y notas. "Guardar y enviar formulario" le
 * manda al dueño el DM con las 4 preguntas clave. Las respuestas del dueño se
 * muestran en solo lectura.
 */
import { useEffect, useMemo, useState } from 'react';
import { useVaultLabels } from './vault-ui';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { trpc } from '@/lib/trpc';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { errMsg } from '@/hooks/useVault';
import {
  CENSUS_DESTINOS,
  CENSUS_ESTADOS,
  CENSUS_QUESTIONS,
  VAULT_AREAS,
  type CensusDestino,
  type CensusEstado,
  type VaultSharedAccount,
} from '../../../../shared/types/vault';

interface SharedAccountDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** null = nueva cuenta */
  account: VaultSharedAccount | null;
}

const NONE = '__none__';
const MFA_UNKNOWN = '__unknown__';

export function SharedAccountDialog({ open, onOpenChange, account }: SharedAccountDialogProps) {
  const { t } = useTranslation();
  const L = useVaultLabels();
  const utils = trpc.useUtils();
  const members = trpc.community.getMembers.useQuery({ status: ['Activo'] }, { staleTime: 5 * 60_000, enabled: open });

  const [accountEmail, setAccountEmail] = useState('');
  const [owner, setOwner] = useState('');
  const [area, setArea] = useState<string>(NONE);
  const [destino, setDestino] = useState<CensusDestino>('relevar');
  const [estado, setEstado] = useState<CensusEstado>('sin_relevar');
  const [probableUse, setProbableUse] = useState('');
  const [declaredUse, setDeclaredUse] = useState('');
  const [hasMfa, setHasMfa] = useState<string>(MFA_UNKNOWN);
  const [mfaLocation, setMfaLocation] = useState('');
  const [platforms, setPlatforms] = useState('');
  const [notes, setNotes] = useState('');

  useEffect(() => {
    if (!open) return;
    setAccountEmail(account?.accountEmail ?? '');
    setOwner(account?.ownerEmail ?? '');
    setArea(account?.area ?? NONE);
    setDestino(account?.destino ?? 'relevar');
    setEstado(account?.estado ?? 'sin_relevar');
    setProbableUse(account?.probableUse ?? '');
    setDeclaredUse(account?.declaredUse ?? '');
    setHasMfa(account?.hasMfa == null ? MFA_UNKNOWN : account.hasMfa ? 'yes' : 'no');
    setMfaLocation(account?.mfaLocation ?? '');
    setPlatforms((account?.platforms ?? []).join(', '));
    setNotes(account?.notes ?? '');
  }, [open, account]);

  const memberOptions = useMemo(
    () =>
      (members.data?.data ?? [])
        .filter((m) => m.email)
        .map((m) => ({ email: m.email.toLowerCase(), label: `${[m.name, m.lastName].filter(Boolean).join(' ')} · ${m.area ?? ''}`.trim() }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [members.data]
  );

  const upsert = trpc.vaultAdmin.upsertSharedAccount.useMutation();
  const assign = trpc.vaultAdmin.assignCensusOwner.useMutation();
  const send = trpc.vaultAdmin.sendCensusForm.useMutation();
  const clear = trpc.vaultAdmin.clearCensusOwner.useMutation();
  const busy = upsert.isPending || assign.isPending || send.isPending || clear.isPending;

  const save = async (sendForm: boolean) => {
    const email = accountEmail.trim().toLowerCase();
    if (!email) return;
    try {
      await upsert.mutateAsync({
        accountEmail: email,
        patch: {
          probableUse: probableUse.trim() || null,
          area: area === NONE ? null : area,
          declaredUse: declaredUse.trim() || null,
          destino,
          estado,
          hasMfa: hasMfa === MFA_UNKNOWN ? null : hasMfa === 'yes',
          mfaLocation: mfaLocation.trim() || null,
          platforms: platforms.split(',').map((p) => p.trim()).filter(Boolean),
          notes: notes.trim() || null,
        },
      });
      const newOwner = owner.trim().toLowerCase();
      if (newOwner && newOwner !== (account?.ownerEmail ?? '')) {
        const r = await assign.mutateAsync({ accountEmail: email, ownerEmail: newOwner, sendForm });
        if (sendForm) toast[r.dmSent ? 'success' : 'warning'](r.dmSent ? t('vault.census.form_dm_ok', 'Formulario enviado por Slack') : t('vault.census.form_dm_fail', 'Guardado, pero el DM de Slack falló'));
      } else if (!newOwner && account?.ownerEmail) {
        // Vaciar el campo = quitar el dueño (review F6: antes quedaba el anterior en silencio).
        await clear.mutateAsync({ accountEmail: email });
      } else if (sendForm) {
        const ok = await send.mutateAsync({ accountEmail: email });
        toast[ok ? 'success' : 'warning'](ok ? t('vault.census.form_dm_ok', 'Formulario enviado por Slack') : t('vault.census.form_dm_fail', 'Guardado, pero el DM de Slack falló'));
      }
      if (!sendForm) toast.success(t('vault.census.saved', 'Cuenta guardada'));
      utils.vaultAdmin.listSharedAccounts.invalidate();
      onOpenChange(false);
    } catch (e) {
      toast.error(errMsg(e as { message?: string }));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{account ? account.accountEmail : t('vault.census.new_title', 'Nueva cuenta compartida')}</DialogTitle>
          <DialogDescription>
            {t('vault.census.dialog_sub', 'Dueño funcional (una persona, no un equipo), uso y destino según el árbol A/B/C/D. El formulario le llega al dueño por Slack.')}
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-3 sm:grid-cols-2">
          {!account && (
            <div className="space-y-1 sm:col-span-2">
              <Label>{t('vault.census.col_account', 'Cuenta')}</Label>
              <Input type="email" value={accountEmail} onChange={(e) => setAccountEmail(e.target.value)} placeholder="cuenta@winclap.com" autoFocus />
            </div>
          )}
          <div className="space-y-1 sm:col-span-2">
            <Label>{t('vault.census.owner', 'Dueño propuesto')}</Label>
            <Input list="census-members" type="email" value={owner} onChange={(e) => setOwner(e.target.value)} placeholder={t('vault.census.owner_ph', 'nombre.apellido@winclap.com')} />
            <datalist id="census-members">
              {memberOptions.map((m) => <option key={m.email} value={m.email}>{m.label}</option>)}
            </datalist>
            {account?.notes && !account.ownerEmail && <p className="text-xs text-muted-foreground">{account.notes}</p>}
          </div>
          <div className="space-y-1">
            <Label>{t('vault.census.col_area', 'Área')}</Label>
            <Select value={area} onValueChange={setArea}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE}>—</SelectItem>
                {VAULT_AREAS.map((a) => <SelectItem key={a.id} value={a.id}>{a.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label>{t('vault.census.col_destino', 'Destino propuesto')}</Label>
            <Select value={destino} onValueChange={(v) => setDestino(v as CensusDestino)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{CENSUS_DESTINOS.map((d) => <SelectItem key={d} value={d}>{L.destino(d)}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label>{t('vault.census.col_estado', 'Estado')}</Label>
            <Select value={estado} onValueChange={(v) => setEstado(v as CensusEstado)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{CENSUS_ESTADOS.map((e) => <SelectItem key={e} value={e}>{L.estado(e)}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label>{t('vault.census.mfa', '¿Tiene MFA?')}</Label>
            <Select value={hasMfa} onValueChange={setHasMfa}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={MFA_UNKNOWN}>{t('vault.census.mfa_unknown', 'No sabemos')}</SelectItem>
                <SelectItem value="yes">{t('vault.census.mfa_yes', 'Sí')}</SelectItem>
                <SelectItem value="no">{t('vault.census.mfa_no', 'No')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1 sm:col-span-2">
            <Label>{t('vault.census.mfa_location', 'Dónde vive el segundo factor')}</Label>
            <Input value={mfaLocation} onChange={(e) => setMfaLocation(e.target.value)} placeholder={t('vault.census.mfa_location_ph', 'teléfono de X / Vault / no tiene')} />
          </div>
          <div className="space-y-1 sm:col-span-2">
            <Label>{t('vault.census.probable_use', 'Uso probable (hipótesis)')}</Label>
            <Input value={probableUse} onChange={(e) => setProbableUse(e.target.value)} />
          </div>
          <div className="space-y-1 sm:col-span-2">
            <Label>{t('vault.census.declared_use', 'Uso declarado')}</Label>
            <Textarea value={declaredUse} onChange={(e) => setDeclaredUse(e.target.value)} rows={2} />
          </div>
          <div className="space-y-1 sm:col-span-2">
            <Label>{t('vault.census.platforms', 'Plataformas que alcanza (separadas por coma)')}</Label>
            <Input value={platforms} onChange={(e) => setPlatforms(e.target.value)} placeholder={t('vault.census.platforms_ph', 'Meta, Google Ads, TikTok')} />
          </div>
          <div className="space-y-1 sm:col-span-2">
            <Label>{t('vault.census.notes', 'Notas')}</Label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} />
          </div>
        </div>

        {account?.answers && Object.keys(account.answers).length > 0 && (
          <div className="space-y-2 rounded-md border bg-muted/30 p-3">
            <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              {t('vault.census.answers_title', 'Respuestas del dueño')}
              {account.answeredAt ? ` · ${new Date(account.answeredAt).toLocaleDateString()}` : ''}
            </div>
            {CENSUS_QUESTIONS.map((q) =>
              account.answers?.[q.key] ? (
                <div key={q.key} className="text-sm">
                  <div className="text-xs text-muted-foreground">{L.question(q.key)}</div>
                  <div className="whitespace-pre-wrap">{account.answers[q.key]}</div>
                </div>
              ) : null
            )}
            {account.ownerNote && (
              <div className="text-sm">
                <div className="text-xs text-muted-foreground">{t('vault.census.owner_note', '"No es mía, es de…"')}</div>
                <div className="whitespace-pre-wrap">{account.ownerNote}</div>
              </div>
            )}
          </div>
        )}

        <DialogFooter className="gap-2 sm:justify-between">
          <div className="text-xs text-muted-foreground">
            {account?.formSentAt
              ? t('vault.census.form_sent_at', 'Formulario enviado {{when}}', { when: new Date(account.formSentAt).toLocaleDateString() })
              : account?.formError
                ? <span className="text-rose-600">{t('vault.census.form_failed_detail', 'El DM de Slack falló: {{err}}', { err: account.formError })}</span>
                : ''}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>{t('common.ui.cancel', 'Cancelar')}</Button>
            <Button variant="outline" onClick={() => save(true)} disabled={busy || !owner.trim() || !accountEmail.trim()}>
              {t('vault.census.save_send', 'Guardar y enviar formulario')}
            </Button>
            <Button onClick={() => save(false)} disabled={busy || !accountEmail.trim()}>
              {busy ? t('vault.form.saving', 'Guardando…') : t('vault.census.save', 'Guardar')}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
